import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink } from "lucide-react";
import { SiteShell } from "@/components/site/SiteShell";
import { buildHead, breadcrumbJsonLd } from "@/lib/seo";
import { publicEcosystemPartnersQuery } from "@/lib/query-registry";
import { setEdgeHtmlCache } from "@/lib/cache-headers";
import type { EcosystemPartner } from "@/lib/ecosystem-partners.functions";

export const Route = createFileRoute("/partners")({
  head: () =>
    buildHead({
      locale: "en",
      title: "Shopify & E-commerce Technology Partners · Shugert",
      description:
        "Independent technology partners Shugert recommends for Shopify and e-commerce teams, from AI content production to analytics, retention, and operations.",
      path: "/partners",
      altPath: "/partners",
      jsonLd: breadcrumbJsonLd([
        { name: "Home", path: "/" },
        { name: "Partners", path: "/partners" },
      ]),
    }),
  loader: async ({ context }) => {
    await setEdgeHtmlCache();
    return context.queryClient.ensureQueryData(publicEcosystemPartnersQuery());
  },
  component: PartnersPage,
});

function outboundRel(partner: EcosystemPartner): string {
  return [
    partner.nofollow ? "nofollow" : "",
    partner.sponsored ? "sponsored" : "",
    "noopener",
    "noreferrer",
  ]
    .filter(Boolean)
    .join(" ");
}

function PartnerMark({ partner }: { partner: EcosystemPartner }) {
  if (partner.logo_url) {
    return (
      <img
        src={partner.logo_url}
        alt={`${partner.name} logo`}
        width={160}
        height={72}
        loading="lazy"
        className="max-h-16 max-w-[160px] object-contain"
      />
    );
  }

  return (
    <div
      aria-label={partner.name}
      className="flex h-16 min-w-36 items-center justify-center rounded-xl border border-border bg-background px-5 font-display text-xl font-semibold tracking-tight"
    >
      {partner.name}
    </div>
  );
}

function PartnersPage() {
  const { partners } = Route.useLoaderData() as {
    partners: EcosystemPartner[];
    storageReady: boolean;
  };

  return (
    <SiteShell>
      <section className="border-b border-border bg-background">
        <div className="mx-auto max-w-7xl px-6 py-20 md:py-28">
          <p className="font-mono text-xs font-semibold uppercase tracking-[0.22em] text-muted-foreground">
            Shugert partner ecosystem
          </p>
          <h1 className="mt-5 max-w-4xl font-display text-4xl font-semibold tracking-[-0.04em] text-foreground md:text-6xl">
            Tools we trust around the Shopify stack.
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-7 text-muted-foreground md:text-lg">
            Independent products and technology partners we may recommend when they solve a
            specific problem for an e-commerce team. These are not services operated by Shugert.
          </p>
        </div>
      </section>

      <section className="bg-card/30">
        <div className="mx-auto max-w-7xl px-6 py-16 md:py-24">
          {partners.length === 0 ? (
            <div className="rounded-2xl border border-border bg-background p-8">
              <p className="text-sm text-muted-foreground">
                Our partner directory is being updated. Check back soon.
              </p>
            </div>
          ) : (
            <div className="grid gap-6 lg:grid-cols-2">
              {partners.map((partner) => (
                <article
                  key={partner.id}
                  className="flex h-full flex-col rounded-2xl border border-border bg-background p-7 shadow-sm md:p-9"
                >
                  <div className="flex min-h-20 items-center">
                    <PartnerMark partner={partner} />
                  </div>
                  <p className="mt-7 font-mono text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    {partner.category}
                  </p>
                  <h2 className="mt-3 font-display text-2xl font-semibold tracking-tight text-foreground">
                    {partner.name}
                  </h2>
                  <p className="mt-4 flex-1 text-sm leading-7 text-muted-foreground">
                    {partner.description}
                  </p>
                  <div className="mt-7 border-t border-border pt-6">
                    <a
                      href={partner.website_url}
                      target="_blank"
                      rel={outboundRel(partner)}
                      className="inline-flex items-center gap-2 rounded-full bg-foreground px-5 py-2.5 text-sm font-semibold text-background transition-opacity hover:opacity-85"
                    >
                      Visit {partner.name}
                      <ExternalLink className="h-4 w-4" aria-hidden="true" />
                    </a>
                  </div>
                </article>
              ))}
            </div>
          )}

          <div className="mt-12 max-w-3xl rounded-2xl border border-border bg-background p-6">
            <h2 className="font-display text-lg font-semibold text-foreground">
              Partnership transparency
            </h2>
            <p className="mt-2 text-sm leading-6 text-muted-foreground">
              Some partner links may be affiliate or commercial relationships. Shugert may receive
              compensation when a client signs up through a partner program, at no additional cost
              to the client. We keep partner links appropriately qualified and recommendations
              remain based on fit for the specific e-commerce use case.
            </p>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
